/**
 * one-stop/blocks-queues — the `queues` block: the prototype's "Open right now".
 *
 * The desk card, lifted: the desk name left, the big wait number in the busy tone
 * with "min" beside it and the busy word beneath, then the meta row — green dot +
 * "Open until 5:00 PM", the location, the walk minutes in semibold — and the two
 * buttons: Join the line (primary), Book a time (secondary). Auto-width on
 * desktop, split full-width at 390 px.
 *
 * ONE THING THE PROTOTYPE HAS AND THE WIRE DOES NOT: a one-line "what this desk
 * helps with" under the name ("Math, writing and computer science help").
 * `PortalQueueItem` carries no such field — name, location, busy level, waits,
 * joinable, bookable and nothing else — so the line is absent rather than
 * invented. Raised in the milestone-2 reply as a small, additive contract ask.
 *
 * THE CTA: "Join the line" → `joinQueue(queueId)` → the ticket and the position.
 * The queue item already carries everything the call needs; no field was invented
 * for the button.
 *
 * AND THE TICKET KEEPS ASKING. A joined ticket re-reads its own place in line on
 * an interval and stops at a terminal state — see `QueueCta`. The number a
 * visitor is watching is the one number on this block that must not be a
 * snapshot of the moment they joined.
 */
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import type { PortalQueueItem } from "@v0y4ge/api-contracts";
import { Button } from "@v0y4ge/jrni-ui";
import { isBrowserNavigable, useCta, useOneStopActions } from "./actions";
import type { QueueEntryState, QueueTicket } from "./actions";
import { configBool, configString, configText, nestedConfigText } from "./config";
import {
  Actions,
  BUSY_TONE,
  CopyAction,
  CtaButton,
  CtaFeedback,
  Empty,
  Section,
  SelectableValue,
  SuccessPanel,
  VisitorFields,
  fragmentOf,
  useVisitor,
} from "./primitives";
import { plural, uiStrings } from "./strings";
import type { StringKey, Translate } from "./strings";
import type { BlockRenderProps } from "./types";

/** ui-config — the four sort vocabularies the registry declares for this block. */
const SORT_KEYS: Record<string, StringKey> = {
  "shortest-wait": "queues.sort.shortest-wait",
  name: "queues.sort.name",
  location: "queues.sort.location",
  curated: "queues.sort.curated",
};

/**
 * The states a ticket does not come back from. Reading a position after one of
 * these asks a question that has already been answered, so the poll stops there.
 */
const TERMINAL: ReadonlySet<QueueEntryState> = new Set(["done", "left", "no-show"]);

/**
 * How often a ticket ON SCREEN re-reads its own place in line. Slow enough that a
 * page left open all afternoon is not a load problem, quick enough that "3 ahead"
 * is true when a student looks down at it.
 */
const POSITION_POLL_MS = 20_000;

export function QueuesBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const fragment = fragmentOf(block, "queues");
  const title = configText(block.config, "title", ctx.locale) ?? t("queues.title");
  const subtitle = configText(block.config, "subtitle", ctx.locale) ?? t("queues.subtitle");
  // Registry defaults, MIRRORED not inverted (one-stop-builder kit §5).
  const showWalk = configBool(block.config, "showWalkMinutes", true);
  const showOpenUntil = configBool(block.config, "showOpenUntil", true);
  const showOverflow = configBool(block.config, "showOverflowLine", true);
  const sortKey = SORT_KEYS[configString(block.config, "sort", "shortest-wait")];
  const joinLabel =
    nestedConfigText(block.config, ["ctaLabels", "join"], ctx.locale) ?? t("queues.join");
  const bookLabel =
    nestedConfigText(block.config, ["ctaLabels", "book"], ctx.locale) ?? t("queues.book");

  if (!fragment || fragment.items.length === 0) {
    return (
      <Section title={title} subtitle={subtitle}>
        <Empty>{t("queues.empty")}</Empty>
      </Section>
    );
  }

  // `total` is the count BEFORE the display bound — never inferred from items.
  const more = Math.max(0, fragment.total - fragment.items.length);

  return (
    <Section title={title} subtitle={subtitle} note={sortKey ? t(sortKey) : null}>
      <div className="lv-grid">
        {fragment.items.map((item) => (
          <DeskCard
            key={item.queueId}
            item={item}
            t={t}
            showWalk={showWalk}
            showOpenUntil={showOpenUntil}
            joinLabel={joinLabel}
            bookLabel={bookLabel}
          />
        ))}
      </div>
      {showOverflow && fragment.hasMore && more > 0 ? (
        <p className="lv-overflow">{plural(t, "queues.more", more)}</p>
      ) : null}
    </Section>
  );
}

function DeskCard(props: {
  item: PortalQueueItem;
  t: Translate;
  showWalk: boolean;
  showOpenUntil: boolean;
  joinLabel: string;
  bookLabel: string;
}): JSX.Element {
  const { item, t } = props;
  const tone = BUSY_TONE[item.busyLevel];

  return (
    <div className="lv-card">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
          <span className="lv-card-title">{item.name}</span>
        </span>
        <span style={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          {item.waitMinutes !== null ? (
            <span style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
              <span className="lv-bignum" style={{ color: tone }}>
                {item.waitMinutes}
              </span>
              <span style={{ fontSize: "var(--jrni-font-size-L1)", color: "var(--jrni-color-text-soft)" }}>
                {t("queues.minutes")}
              </span>
            </span>
          ) : null}
          <span
            style={{
              fontSize: "var(--jrni-font-size-L1)",
              fontWeight: "var(--jrni-font-weight-bold)",
              color: tone,
            }}
          >
            {t(`busy.${item.busyLevel}` as StringKey)}
          </span>
        </span>
      </div>

      {/* The prototype's meta row, in its order: open-until with the live dot,
          the location, then the walk in semibold. */}
      <div className="lv-card-meta">
        {props.showOpenUntil && item.openUntil ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              className="lv-dot"
              style={item.open ? undefined : { background: "var(--jrni-color-text-soft)" }}
            />
            {t("queues.openUntil", { t: item.openUntil.time })}
          </span>
        ) : null}
        {item.locationName ? <span>{item.locationName}</span> : null}
        {item.peopleAhead !== null ? <span>{t("queues.ahead", { n: item.peopleAhead })}</span> : null}
        {props.showWalk && item.walkMinutes !== null ? (
          <span style={{ fontWeight: "var(--jrni-font-weight-semibold)" }}>
            {t("queues.walk", { n: item.walkMinutes })}
          </span>
        ) : null}
      </div>

      <QueueCta item={item} t={t} joinLabel={props.joinLabel} bookLabel={props.bookLabel} />
    </div>
  );
}

/**
 * Join the line, in every state: idle → the details → running → the ticket, or
 * the refusal in the page's own words with a retry.
 *
 * WHY THE FORM IS PROGRESSIVE. `joinPublicQueue` REQUIRES a name, and asking for
 * a phone is what makes a double-tapped Join idempotent. But the card at rest is
 * the prototype's card, so the fields appear only after the visitor taps Join —
 * the desk grid is pixel-unchanged until someone commits to the flow.
 *
 * EMAIL IS OPTIONAL HERE, AND THAT IS THE CONTRACT'S CHOICE, NOT A RELAXATION.
 * `QueueJoinRequest.email` is "a valid address or null", so the form asks for it,
 * marks it "Optional", refuses a malformed one BEFORE sending, and omits it from
 * the body when empty. `checkout` and the event registration require it, and
 * their forms say so — same component, different operation.
 *
 * The entry token comes back from the join and lives HERE, in this component's
 * state, for as long as the card is mounted. It is never written to the URL, to
 * storage, or to a module singleton — inside a third-party embed the token has
 * to be held by the frame's own code, and a shared singleton is how that becomes
 * a cross-tab leak. What goes ON SCREEN is the ticket and the check-in code.
 *
 * `recoveredExisting` is rendered as its own state: a join that matched an
 * existing active entry returns THAT entry with HTTP 200, and the honest
 * sentence is "you're already in this line", not a second confirmation.
 */
function QueueCta(props: {
  item: PortalQueueItem;
  t: Translate;
  joinLabel: string;
  bookLabel: string;
}): JSX.Element | null {
  const { item, t } = props;
  const actions = useOneStopActions();
  const visitor = useVisitor({ requireEmail: false });
  const [collecting, setCollecting] = useState(false);
  const [ticket, setTicket] = useState<QueueTicket | null>(null);

  const join = useCallback(
    () => actions.joinQueue({ queueId: item.queueId, visitor: visitor.value }),
    [actions, item.queueId, visitor.value],
  );
  const cta = useCta(join, setTicket, visitor.showServerErrors);

  const leave = useCallback(
    () =>
      ticket
        ? actions.leaveQueue({ queueId: item.queueId, positionToken: ticket.positionToken })
        : Promise.resolve(),
    [actions, item.queueId, ticket],
  );
  const leaveCta = useCta(leave, () => {
    setTicket(null);
    setCollecting(false);
    cta.reset();
  });

  /**
   * A JOINED TICKET ASKS AGAIN.
   *
   * `queuePosition` was declared on `OneStopActions`, implemented by
   * `livePortalActions`, and called by NOTHING — so this panel rendered the join
   * response's first frame and never refreshed. On a block whose whole promise is
   * "these are the waits at the moment", the number a visitor actually watches
   * was the one number frozen at the moment they joined (CC 0020 finding 5).
   *
   * The credential is the entry token the join returned, and it stays exactly
   * where it was: this component's state, out on the read and nowhere else —
   * never a URL, never storage, never a module singleton.
   *
   * The effect keys on the TOKEN and the STATE rather than the ticket object, so
   * a refreshed position does not tear down and re-arm the interval every cycle;
   * reaching a terminal state does stop it, because that dep changed and the
   * guard returns. A failed read is swallowed on purpose: "a ticket is on screen"
   * and "one poll missed" are different facts, and blanking a visitor's place in
   * line over the second would be the worse of the two errors.
   */
  const positionToken = ticket?.positionToken ?? null;
  const ticketState = ticket?.state ?? null;
  useEffect(() => {
    if (positionToken === null || ticketState === null) return undefined;
    if (TERMINAL.has(ticketState)) return undefined;
    if (!actions.available) return undefined;
    let live = true;
    const id = window.setInterval(() => {
      void actions
        .queuePosition({ queueId: item.queueId, positionToken })
        .then((position) => {
          if (!live) return;
          setTicket((current) =>
            current === null || current.positionToken !== positionToken
              ? current
              : {
                  ...current,
                  reference: position.reference,
                  peopleAhead: position.peopleAhead,
                  estimatedWaitMinutes: position.estimatedWaitMinutes,
                  state: position.state,
                },
          );
        })
        .catch(() => undefined);
    }, POSITION_POLL_MS);
    return () => {
      live = false;
      window.clearInterval(id);
    };
  }, [actions, item.queueId, positionToken, ticketState]);

  if (!item.joinable && !item.bookable) return null;

  // Joining is LIVE now, so the only reason left is "nothing may be called
  // here" — the builder preview. That is a different sentence from "this flow
  // isn't built yet", and using the old one would tell an admin the desk cards
  // are unfinished when they are simply not fireable from a preview.
  const joinReason = actions.available ? null : actions.unavailableReason || t("cta.preview");
  // A `bookable` queue card carries no `serviceId` (kit §2.2e), so there is
  // nothing to hand to availability. The affordance stays visible with its
  // reason rather than silently doing nothing — an author who wants a live Book
  // here places a sibling `book-appointment` block naming the service.
  const bookReason = actions.unavailableReason || t("cta.pending.book");
  /**
   * The line under the cluster describes the CONTROLS THAT ARE THERE. It used to
   * be `joinReason ?? bookReason`, which falls through whenever joining is fine —
   * so a join-only desk printed "Booking opens with the next drop" under a card
   * with no Book button on it at all (CC 0020 finding 7). The book reason is now
   * reached only when the card actually renders the Book control it is about.
   */
  const clusterReason = joinReason ?? (item.bookable ? bookReason : null);

  /* HANDOFF: Join opens the walk-in journey on THIS desk, in a new tab.

     The inline join below is not dead code — it is what an embed with no
     journey page to open still uses. When the host did install a handoff, the
     card stops collecting a name and a phone: the walk-in journey asks for what
     it needs (and asks for it under its own published copy and consent), and a
     front door that collected two fields and then handed them nowhere would be
     asking twice. The queue id travels; the service id travels too WHEN the
     document carries one, which today it does not (kit §2.2e). */
  // A HAND-OFF IS INSTALLED BUT THIS DESK HAS NO USABLE DESTINATION: fall
  // through to the inline join below rather than opening a link that goes
  // nowhere. The portal host answers 200 for every path, so a dead hand-off
  // does not fail visibly — it returns the visitor to this page. The inline
  // flow works.
  //
  // The test is `isBrowserNavigable`, NOT truthiness, because `handoffUrl`
  // applies that same test before it will use the value. Asking a weaker
  // question here would let a truthy-but-unnavigable URL take this branch,
  // suppress the inline flow, and then fall through to the composed path —
  // reintroducing the dead hand-off this guard exists to prevent.
  if (actions.handoff && item.joinUrl && isBrowserNavigable(item.joinUrl)) {
    const hand = actions.handoff;
    const carry = {
      url: item.joinUrl,
      queueId: item.queueId,
      serviceId: (item as { serviceId?: string | null }).serviceId ?? null,
      locationId: item.locationId ?? null,
      label: item.name,
    };
    return (
      <Actions>
        {item.joinable ? (
          <span className="lv-act">
            <Button
              hierarchy="primary"
              size="small"
              onClick={() => hand({ kind: "queue", ...carry })}
            >
              {props.joinLabel}
            </Button>
          </span>
        ) : null}
        {item.bookable ? (
          <span className="lv-act">
            {/* A bookable desk hands off to the APPOINTMENT journey, and it can
                only do that when the document named a service — the reason this
                control has always carried one. */}
            {carry.serviceId ? (
              <Button
                hierarchy="secondary"
                size="small"
                onClick={() =>
                  hand({
                    kind: "appointment",
                    serviceId: carry.serviceId as string,
                    locationId: carry.locationId,
                    label: carry.label,
                  })
                }
              >
                {props.bookLabel}
              </Button>
            ) : (
              <CtaButton
                label={props.bookLabel}
                onClick={() => undefined}
                t={t}
                hierarchy="secondary"
                disabledReason={bookReason}
                running={false}
              />
            )}
          </span>
        ) : null}
      </Actions>
    );
  }

  if (ticket) {
    /* The wait is the only thing left on the meta LINE. The check-in code moved
       to its own row below, because it is one of the two things a visitor reads
       out at a desk or types into another device — it needs to be selectable on
       its own and to have Copy beside it, and neither is possible while it is
       one clause of a joined sentence. Nothing about the wait changed. */
    const meta =
      ticket.estimatedWaitMinutes !== null
        ? t("queue.joined.wait", { n: ticket.estimatedWaitMinutes })
        : null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SuccessPanel
          eyebrow={ticket.recovered ? t("queue.already") : t("queue.joined.eyebrow")}
          headline={
            <>
              <SelectableValue
                sentence={t("queue.joined.ticket", { ref: ticket.reference })}
                value={ticket.reference}
              />
              <CopyAction
                t={t}
                value={ticket.reference}
                label={t("copy.ticket", { ref: ticket.reference })}
              />
            </>
          }
          meta={meta}
          aside={
            ticket.peopleAhead !== null
              ? { value: String(ticket.peopleAhead), label: t("queue.joined.ahead") }
              : null
          }
        >
          {ticket.checkInCode ? (
            <span className="lv-success-meta">
              <SelectableValue
                sentence={t("queue.joined.code", { code: ticket.checkInCode })}
                value={ticket.checkInCode}
              />
              <CopyAction
                t={t}
                value={ticket.checkInCode}
                label={t("copy.code", { code: ticket.checkInCode })}
              />
            </span>
          ) : null}
        </SuccessPanel>
        <Actions>
          <span className="lv-act">
            <Button
              variant="ghost"
              size="small"
              onClick={leaveCta.run}
              disabled={leaveCta.state.status === "running"}
            >
              {leaveCta.state.status === "running" ? t("cta.working") : t("queue.leave")}
            </Button>
          </span>
        </Actions>
        <CtaFeedback t={t} disabledReason={null} state={leaveCta.state} />
      </div>
    );
  }

  const running = cta.state.status === "running";

  if (collecting) {
    // The pre-flight, and the reason no request leaves for a malformed address:
    // `check()` marks the offending fields, focuses the first, and answers false.
    const submit = (): void => {
      if (visitor.check()) cta.run();
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <VisitorFields form={visitor} t={t} askPhone disabled={running} />
        <Actions>
          <CtaButton
            label={cta.state.status === "error" ? t("cta.retry") : props.joinLabel}
            onClick={submit}
            t={t}
            hierarchy="primary"
            disabledReason={joinReason}
            running={running}
          />
          <span className="lv-act">
            <Button
              variant="ghost"
              size="small"
              onClick={() => setCollecting(false)}
              disabled={running}
            >
              {t("visitor.cancel")}
            </Button>
          </span>
        </Actions>
        <CtaFeedback t={t} disabledReason={joinReason} state={cta.state} />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Actions>
        {item.joinable ? (
          <CtaButton
            label={props.joinLabel}
            onClick={() => setCollecting(true)}
            t={t}
            hierarchy="primary"
            disabledReason={joinReason}
          />
        ) : null}
        {item.bookable ? (
          <CtaButton
            label={props.bookLabel}
            onClick={() => undefined}
            t={t}
            hierarchy="secondary"
            disabledReason={bookReason}
          />
        ) : null}
      </Actions>
      <CtaFeedback t={t} disabledReason={clusterReason} state={cta.state} />
    </div>
  );
}
